import { isCommandError } from '@st/sdk'
import { Effect } from 'effect'
import { ErrorReporting } from '~/effects/error-reporting'

type ConsoleMode = 'never' | 'dev' | 'always'

export type ReportHandledErrorOptions = {
  source: string
  operation: string
  tags?: Record<string, string | number | boolean | undefined>
  extra?: unknown
  /**
   * Default false to avoid duplicate captures when SDK boundary capture is enabled.
   */
  captureSdkErrors?: boolean
  /**
   * Default 'dev' to keep local debugging visible without spamming production logs.
   */
  console?: ConsoleMode
  /**
   * Optional final gate for expected errors that should not be captured.
   */
  shouldCapture?: (error: unknown) => boolean
}

export function reportHandledError(
  error: unknown,
  options: ReportHandledErrorOptions
): Effect.Effect<void, never, ErrorReporting> {
  const logEffect = shouldLogToConsole(options.console ?? 'dev')
    ? Effect.logError(`[${options.source}] ${options.operation}`, error)
    : Effect.void

  if (!shouldCaptureError(error, options)) {
    return logEffect
  }

  return Effect.gen(function* () {
    const errorReporting = yield* ErrorReporting
    yield* logEffect
    yield* errorReporting.captureException(error, {
      tags: {
        handled: true,
        source: options.source,
        operation: options.operation,
        ...options.tags
      },
      extra: options.extra === undefined ? { operation: options.operation } : options.extra
    })
  })
}

function shouldCaptureError(error: unknown, options: ReportHandledErrorOptions): boolean {
  if (options.shouldCapture && !options.shouldCapture(error)) {
    return false
  }

  if (options.captureSdkErrors) {
    return true
  }

  return !isCommandError(error)
}

function shouldLogToConsole(mode: ConsoleMode): boolean {
  switch (mode) {
    case 'always':
      return true
    case 'never':
      return false
    case 'dev':
      return Boolean(import.meta.env.DEV)
  }
}
